import React, { useEffect } from 'react';

import { Clients } from '@ariksa/notification';
import { DataTypeForES, ReportTypes } from '@ariksa/reporting';
import { Box, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { OutlinedIconButton, Select } from 'components/DataEntry';
import {
  DownloadIcon,
  ExportIcon,
  PrinterIcon,
  VulnerabilityIcon,
} from 'components/Icons';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { ExportAlertsModal } from 'containers/SharedState/Components/ExportAlerts/ExportAlertsModal';
import { useSharedStateContext } from 'containers/SharedState/context/context';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { VulnerabilityDashboard } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/VulnerabilityDashboard';
import { showOptions } from 'containers/Visibility/Vulnerabilities/Components/utils';
import { VulnerabilityTabs } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/VulnerabilityTabs';
import { useSearchParams } from 'hooks/useSearchParams';
import { formatDate } from 'utils/date';

interface Props {
  useVulnerabilityV3?: boolean;
}

export const VulnerabilitySideNav: React.FC<Props> = ({
  useVulnerabilityV3 = false,
}) => {
  const [show, setShow] = React.useState('Summary');
  const params = useSearchParams<{
    show: string;
    //search_term: string;
  }>();
  const { updateValueInURL } = useSearchParamFunctions();
  const navigate = useNavigate();
  const { contentRef } = useSharedStateContext();
  const exportAlerts = useDisclosure();
  const dispatch = useDispatch();
  const { clients } = useSelector(selectSharedState);

  useEffect(() => {
    if (!!params.show) {
      setShow(params.show);
    }
  }, [params.show]);

  useEffect(() => {
    dispatch(
      sharedStateActions.getClients({ clientName: Clients.Elasticsearch }),
    );
  }, [dispatch]);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle:
      formatDate(new Date().toISOString()) +
      ' Visibility Vulnerability Dashboard',
    pageStyle: `
      @page {
        layout: landscape;
        margin: 0;
        size: 18in 11in;
      }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    `,
  });

  return (
    <Card>
      <Stack spacing="headerContentSpacing" h="full" w="full">
        <HStack justify="space-between">
          <HStack spacing={6} pl={3}>
            <PageHeaderWithIcon
              label="Vulnerabilities"
              icon={<VulnerabilityIcon />}
            />
            <HStack>
              <Box color="primary">Show</Box>
              <Box w={40}>
                <Select
                  options={showOptions}
                  value={show === 'Details' ? showOptions[1] : showOptions[0]}
                  onChange={option => {
                    updateValueInURL('show', option?.value);
                  }}
                  showClearIndicator={false}
                  showIconInValueContainer
                />
              </Box>
            </HStack>
          </HStack>
          {show !== 'Details' ? (
            <OutlinedIconButton
              leftIcon={<PrinterIcon />}
              onClick={() => handlePrint()}
            >
              PDF
            </OutlinedIconButton>
          ) : (
            <HStack>
              <OutlinedIconButton
                leftIcon={<DownloadIcon />}
                onClick={() => {
                  navigate(
                    '/reports/all?addReport=true&reportType=' +
                      ReportTypes.VulnerabilitiesDetailed,
                  );
                }}
              >
                Get Report
              </OutlinedIconButton>
              <OutlinedIconButton
                leftIcon={<ExportIcon />}
                onClick={exportAlerts.onOpen}
                // Export needs at least one Elasticsearch (SIEM) client configured.
                isDisabled={!clients.elasticsearch?.length || clients.loading}
                isLoading={clients.loading}
                tooltip="Export all the vulnerabilities to SIEM"
                tooltipStyles={{ tooltip: { w: 32, textAlign: 'center' } }}
              >
                Export to SIEM
              </OutlinedIconButton>
            </HStack>
          )}
        </HStack>
        {show !== 'Summary' ? (
          <VulnerabilityTabs />
        ) : (
          <VulnerabilityDashboard
            setShow={setShow}
            useVulnerabilityV3={useVulnerabilityV3}
          />
        )}
      </Stack>
      {exportAlerts.isOpen && (
        <ExportAlertsModal
          isOpen={exportAlerts.isOpen}
          type={DataTypeForES.Vulnerabilities}
          onClose={exportAlerts.onClose}
        />
      )}
    </Card>
  );
};
